import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import DiscoverMoreSection from "@/components/DiscoverMoreSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Marquee } from "@/components/ui/marquee";
import { ContainerScroll } from "@/components/ui/container-scroll-animation";
import { HowItWorksSection } from "@/components/ui/how-it-works-section";
import { Zap, Target, Sparkles, FileText, Edit3, TrendingUp, Lightbulb, Search, BarChart3, ShoppingCart, PenTool, Mail, BookOpen } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { navigateToEnterpriseDemo } from "@/utils/navigation";
import CTASection from "@/components/CTASection";
function AgentsPage() {
  const navigate = useNavigate();

  const why = [
    { icon: Target, title: "Specialists, not generalists", desc: "Each Agent is trained for a specific marketing job." },
    { icon: Zap, title: "Built on industry briefs", desc: "Industry-standard brief inputs and output formats for every Agent." },
    { icon: Sparkles, title: "Tailored to your brand + channel", desc: "Brand Vault and purpose best practices applied automatically." },
  ];

  const _flow = [
    "Brief manually or Auto-Brief",
    "Multiple outputs",
    "Recommended Result",
    "Edit",
    "Publish",
  ];

  const allAgents = [
    { name: "Marketing Strategy Agent", description: "Spin up a boardroom-ready marketing roadmap in minutes—AI-curated insights, channel-smart", icon: TrendingUp },
    { name: "Ideation Agent", description: "Ignite breakthrough thinking. gimmefy blends real-time market intel with punchy creative", icon: Lightbulb },
    { name: "Ultimate PR Agent", description: "Command the headlines! gimmefy distills trend-setting intel into bespoke PR plays,", icon: FileText },
    { name: "Research Agent", description: "Get your research right—gimmefy digs deep for market intel, surfaces breakout trends, and", icon: Search },
    { name: "Performance Agent", description: "Flip data into profit. gimmefy scans every metric, spotlights hidden wins, and drops", icon: BarChart3 },
    { name: "E-Commerce Agent", description: "Turbocharge your storefront! gimmefy scripts bespoke marketing moves, conversion-first", icon: ShoppingCart },
    { name: "Blog Agent", description: "Turn raw sparks into narratives—gimmefy crafts your brand voice into binge-worthy blog posts", icon: BookOpen },
    { name: "Copywriting Agent", description: "Spin immersive stories! gimmefy weaves brand-true narratives that resonate deeply,", icon: PenTool },
    { name: "Text Editor Agent", description: "Cut through the clutter—gimmefy smooths every sentence into crystal-clear, tone-true", icon: Edit3 },
    { name: "Email Agent", description: "Win hearts—and clicks. gimmefy tailors every subject line, stitches in hyper-personal touches", icon: Mail },
    { name: "Newsletter Agent", description: "Serve inbox gold! gimmefy assembles value-rich newsletters with perfectly timed touchpoints", icon: Mail },
    { name: "Script Agent", description: "Roll film-worthy stories—gimmefy conceives visionary video concepts, crafts engaging", icon: Sparkles },
  ];

  // Split agents into 3 rows
  const agentRows = [
    allAgents.slice(0, 4),
    allAgents.slice(4, 8),
    allAgents.slice(8, 12),
  ];

  return (
    <div className="min-h-screen bg-background mt-24 md:mt-0">
      <Navigation />

      <div style={{
        '--brand': '200 95% 48%',
        '--brand-foreground': '205 90% 60%'
      } as React.CSSProperties}>
        <ContainerScroll
          titleComponent={
            <>
              <div className="mb-4">
                <Badge className="bg-primary/10 text-primary border-primary/20">
                  Agents
                </Badge>
              </div>
              <h1 className="text-4xl font-semibold text-black dark:text-white mb-4">
              Marketing tasks - simplified with proven marketing AI Agents.
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                From SEO to video scripts, every Agent is trained on proven marketing frameworks.
              </p>
              <div className="flex justify-center mt-8">
                <Button id="agents-book-demo-button" className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors h-12" onClick={() => navigateToEnterpriseDemo(navigate)}>
                  Book an Agents Demo
                </Button>
              </div>
            </>
          }
        >
          <img
            src="/lovable-uploads/e64282f4-5403-4f77-813c-4b5543edffc4.png"
            alt="Agents interface showing gimmefy's Ultimate Toolkit and specialized marketing agents"
            className="mx-auto rounded-2xl object-cover h-full object-center"
            draggable={false}
            width={888}
            height={472}
          />
        </ContainerScroll>
      </div>

      {/* Section 1: Why Agents Exist */}
      <section className="py-16 bg-muted/30 mt-[135px]">
        <div className="container mx-auto px-6 grid md:grid-cols-3 gap-6">
          {why.map((w, i) => (
            <Card key={i} className="h-full">
              <CardContent className="p-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary border border-primary/20 grid place-items-center mb-4">
                  <w.icon className="w-5 h-5" />
                </div>
                <div className="font-semibold mb-2">{w.title}</div>
                <p className="text-sm text-muted-foreground">{w.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Section 2: How Agents Work */}
      <HowItWorksSection
        title="How Agents Work"
        steps={[
          "Brief manually or Auto-Brief",
          "Multiple outputs generated",
          "AI recommends best result",
          "Edit and refine content",
          "Publish across channels"
        ]}
        image={{
          src: "/lovable-uploads/cbe05af2-88ef-4fec-bd27-e8d1dcbe34db.jpg",
          alt: "Content refinement interface showing LinkedIn campaign optimization with copywriting and image generation tools"
        }}
      />

      <CTASection title="Know someone who’d love an AI upgrade?"  subtitle="Share a special invite so they can meet gimmefy’s smart AI Agents—and get extra signup credits to experiment!" buttonText="Tell a friend" buttonLink="/tellafriend" />

      {/* Section 3: gimmefy's Ultimate Toolkit */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">gimmefy's Ultimate Toolkit</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Meet the fan favorites! This is your go-to lineup of top-used, highest-praised gimmefy Agents
            </p>
          </div>

          <div className="space-y-6 overflow-x-auto">
            {agentRows.map((row, rowIndex) => (
              <Marquee
                key={rowIndex}
                speed="normal"
                reverse={rowIndex % 2 === 1}
                pauseOnHover={true}
                className="py-2 cursor-grab active:cursor-grabbing"
              >
                {row.map((agent, index) => {
                  const colorVariants = [
                    'bg-blue-500/10 text-blue-600 border-blue-500/20',
                    'bg-green-500/10 text-green-600 border-green-500/20',
                    'bg-orange-500/10 text-orange-600 border-orange-500/20',
                    'bg-purple-500/10 text-purple-600 border-purple-500/20',
                    'bg-pink-500/10 text-pink-600 border-pink-500/20',
                    'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
                    'bg-red-500/10 text-red-600 border-red-500/20',
                    'bg-indigo-500/10 text-indigo-600 border-indigo-500/20',
                    'bg-teal-500/10 text-teal-600 border-teal-500/20',
                    'bg-cyan-500/10 text-cyan-600 border-cyan-500/20',
                    'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
                    'bg-violet-500/10 text-violet-600 border-violet-500/20'
                  ];
                  const colorIndex = (rowIndex * 4 + index) % colorVariants.length;

                  return (
                    <Card key={`${rowIndex}-${index}`} className="mx-3 w-80 flex-shrink-0">
                      <CardContent className="p-6 flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg ${colorVariants[colorIndex]} grid place-items-center flex-shrink-0`}>
                          <agent.icon className="w-5 h-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold mb-1">{agent.name}</div>
                          <p className="text-sm text-muted-foreground line-clamp-2">{agent.description}</p>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </Marquee>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-primary/10 via-primary/5 to-background text-center">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">Your specialists are standing by.</h2>
          <Button size="lg" onClick={() => navigateToEnterpriseDemo(navigate)}>
            See Agents in action
          </Button>
        </div>
      </section>

      <DiscoverMoreSection currentPage="agents" />

      <FooterWithNewsletter />
    </div>
  );
}

export default AgentsPage;
